import {
  SHIFT_STATUS,
  normalizeShiftStatus,
  getAssignedWorkerUid,
  hasWorkerAcceptanceEvidence,
  hasShiftArrivalEvidence,
  getShiftStartMs,
  getShiftEndMs,
  isTerminalShiftStatus,
} from "./shiftLifecycle";

/* ─────────────────────────────
   VALIDATION RESULT
───────────────────────────── */

function validationResult(
  valid,
  code = "",
  message = ""
) {
  return {
    valid,
    code,
    message,
  };
}

/* ─────────────────────────────
   SHIFT SCOPE
───────────────────────────── */

export function validateShiftScope(
  shift
) {
  if (!shift) {
    return validationResult(
      false,
      "missing_shift",
      "Shift is required"
    );
  }

  const tenantId = String(
    shift?.tenantId || ""
  )
    .trim()
    .toLowerCase();

  const facilityId = String(
    shift?.facilityId || ""
  )
    .trim()
    .toLowerCase();

  if (!tenantId) {
    return validationResult(
      false,
      "missing_tenant",
      "Shift is missing tenantId"
    );
  }

  if (!facilityId) {
    return validationResult(
      false,
      "missing_facility",
      "Shift is missing facilityId"
    );
  }

  return validationResult(
    true
  );
}

export function assertValidShiftScope(
  shift
) {
  const result =
    validateShiftScope(shift);

  if (!result.valid) {
    const error =
      new Error(
        result.message
      );

    error.code =
      result.code;

    throw error;
  }

  return true;
}

/* ─────────────────────────────
   WORKER / TENANT SCOPE
───────────────────────────── */

export function validateWorkerShiftTenant({
  worker,
  shift,
}) {
  const shiftTenantId =
    String(
      shift?.tenantId || ""
    )
      .trim()
      .toLowerCase();

  const workerTenantId =
    String(
      worker?.tenantId || ""
    )
      .trim()
      .toLowerCase();

  if (!shiftTenantId) {
    return validationResult(
      false,
      "missing_shift_tenant",
      "Shift is missing tenantId"
    );
  }

  /*
   * Legacy workers may not yet have tenantId.
   * When present, it must match.
   */
  if (
    workerTenantId &&
    workerTenantId !==
      shiftTenantId
  ) {
    return validationResult(
      false,
      "tenant_mismatch",
      "Worker and shift belong to different tenants"
    );
  }

  return validationResult(
    true
  );
}

export function assertWorkerShiftTenant({
  worker,
  shift,
}) {
  const result =
    validateWorkerShiftTenant({
      worker,
      shift,
    });

  if (!result.valid) {
    const error =
      new Error(
        result.message
      );

    error.code =
      result.code;

    throw error;
  }

  return true;
}

/* ─────────────────────────────
   TIME RANGE
───────────────────────────── */

export function validateShiftTimeRange({
  startMs,
  endMs,
}) {
  if (
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs)
  ) {
    return validationResult(
      false,
      "invalid_time_range",
      "Invalid shift time range"
    );
  }

  if (endMs <= startMs) {
    return validationResult(
      false,
      "invalid_time_order",
      "Shift end time must be after start time"
    );
  }

  return validationResult(
    true
  );
}

export function assertValidShiftTimeRange({
  startMs,
  endMs,
}) {
  const result =
    validateShiftTimeRange({
      startMs,
      endMs,
    });

  if (!result.valid) {
    const error =
      new Error(
        result.message
      );

    error.code =
      result.code;

    throw error;
  }

  return true;
}

/* ─────────────────────────────
   TIME EDIT ELIGIBILITY
───────────────────────────── */

const TIME_EDIT_BLOCKED_STATUSES =
  new Set([
    SHIFT_STATUS.ACTIVATED,
    SHIFT_STATUS.TRACKING,
    SHIFT_STATUS.ARRIVED,
    SHIFT_STATUS.COMPLETED,
    SHIFT_STATUS.NO_SHOW,
    SHIFT_STATUS.CANCELED,
    SHIFT_STATUS.CANCELLED,
    SHIFT_STATUS.CLOSED_UNFILLED,
  ]);

export function validateShiftTimeEditable(
  shift
) {
  if (!shift) {
    return validationResult(
      false,
      "missing_shift",
      "Shift is required"
    );
  }

  const status =
    normalizeShiftStatus(
      shift?.status
    );

  if (
    TIME_EDIT_BLOCKED_STATUSES.has(
      status
    )
  ) {
    return validationResult(
      false,
      "time_edit_not_allowed",
      "Cannot edit time for this shift status"
    );
  }

  return validationResult(
    true
  );
}

export function assertShiftTimeEditable(
  shift
) {
  const result =
    validateShiftTimeEditable(
      shift
    );

  if (!result.valid) {
    const error =
      new Error(
        result.message
      );

    error.code =
      result.code;

    throw error;
  }

  return true;
}

/* ─────────────────────────────
   ASSIGNMENT ELIGIBILITY
───────────────────────────── */

export function validateShiftAssignable(
  shift
) {
  if (!shift) {
    return validationResult(
      false,
      "missing_shift",
      "Shift is required"
    );
  }

  const status =
    normalizeShiftStatus(
      shift?.status
    );

  if (
    status !==
    SHIFT_STATUS.OPEN
  ) {
    return validationResult(
      false,
      "shift_not_open",
      "Shift is not open"
    );
  }

  const assignedWorkerUid =
    getAssignedWorkerUid(
      shift
    );

  if (assignedWorkerUid) {
    return validationResult(
      false,
      "shift_already_assigned",
      "Shift already taken"
    );
  }

  return validationResult(
    true
  );
}

export function assertShiftAssignable(
  shift
) {
  const result =
    validateShiftAssignable(
      shift
    );

  if (!result.valid) {
    const error =
      new Error(
        result.message
      );

    error.code =
      result.code;

    throw error;
  }

  return true;
}

/* ─────────────────────────────
   REASSIGNMENT ELIGIBILITY
───────────────────────────── */

export function validateShiftReassignable(
  shift
) {
  if (!shift) {
    return validationResult(
      false,
      "missing_shift",
      "Shift is required"
    );
  }

  if (
    isTerminalShiftStatus(
      shift?.status
    )
  ) {
    return validationResult(
      false,
      "closed_shift",
      "Cannot reassign a closed shift"
    );
  }

  return validationResult(
    true
  );
}

export function assertShiftReassignable(
  shift
) {
  const result =
    validateShiftReassignable(
      shift
    );

  if (!result.valid) {
    const error =
      new Error(
        result.message
      );

    error.code =
      result.code;

    throw error;
  }

  return true;
}

/* ─────────────────────────────
   NO-SHOW ELIGIBILITY
───────────────────────────── */

const NO_SHOW_STATUSES =
  new Set([
    SHIFT_STATUS.ASSIGNED,
    SHIFT_STATUS.ACTIVATED,
    SHIFT_STATUS.TRACKING,
  ]);

export function validateNoShowEligibility(
  shift,
  nowMs = Date.now()
) {
  if (!shift) {
    return validationResult(
      false,
      "missing_shift",
      "Shift is required"
    );
  }

  const status =
    normalizeShiftStatus(
      shift?.status
    );

  if (
    !NO_SHOW_STATUSES.has(
      status
    )
  ) {
    return validationResult(
      false,
      "no_show_status_invalid",
      "Shift is not eligible to be marked no-show"
    );
  }

  const assignedWorkerUid =
    getAssignedWorkerUid(
      shift
    );

  if (!assignedWorkerUid) {
    return validationResult(
      false,
      "no_assigned_worker",
      "Cannot mark an unassigned shift as worker no-show"
    );
  }

  if (
    !hasWorkerAcceptanceEvidence(
      shift,
      assignedWorkerUid
    )
  ) {
    return validationResult(
      false,
      "worker_not_accepted",
      "Cannot mark no-show before worker acceptance"
    );
  }

  if (
    hasShiftArrivalEvidence(
      shift
    )
  ) {
    return validationResult(
      false,
      "arrival_already_recorded",
      "Arrived shift cannot be marked no-show"
    );
  }

  const startMs =
    getShiftStartMs(
      shift
    );

  if (
    !Number.isFinite(
      startMs
    )
  ) {
    return validationResult(
      false,
      "invalid_start_time",
      "Shift start time invalid"
    );
  }

  if (nowMs < startMs) {
    return validationResult(
      false,
      "shift_not_started",
      "Cannot mark no-show before shift start"
    );
  }

  return validationResult(
    true
  );
}

export function assertNoShowEligibility(
  shift,
  nowMs = Date.now()
) {
  const result =
    validateNoShowEligibility(
      shift,
      nowMs
    );

  if (!result.valid) {
    const error =
      new Error(
        result.message
      );

    error.code =
      result.code;

    throw error;
  }

  return true;
}

/* ─────────────────────────────
   ACTIVATION ELIGIBILITY
───────────────────────────── */

export function validateShiftActivation({
  shift,
  workerUid,
  nowMs = Date.now(),
  activationWindowHours = 2,
}) {
  if (!shift) {
    return validationResult(
      false,
      "missing_shift",
      "Shift is required"
    );
  }

  const assignedWorkerUid =
    getAssignedWorkerUid(
      shift
    );

  if (
    !assignedWorkerUid ||
    assignedWorkerUid !==
      workerUid
  ) {
    return validationResult(
      false,
      "worker_reassigned",
      "This shift was reassigned."
    );
  }

  const status =
    normalizeShiftStatus(
      shift?.status
    );

  if (
    status ===
    SHIFT_STATUS.ACTIVATED
  ) {
    return validationResult(
      true
    );
  }

  if (
    status !==
    SHIFT_STATUS.ASSIGNED
  ) {
    return validationResult(
      false,
      "activation_not_allowed",
      "Shift cannot be activated"
    );
  }

  if (
    !hasWorkerAcceptanceEvidence(
      shift,
      workerUid
    )
  ) {
    return validationResult(
      false,
      "worker_not_accepted",
      "Worker must accept the shift before activation"
    );
  }

  const startMs =
    getShiftStartMs(
      shift
    );

  if (
    !Number.isFinite(
      startMs
    )
  ) {
    return validationResult(
      false,
      "invalid_start_time",
      "Shift start time invalid"
    );
  }

  const endMs = getShiftEndMs(shift);
  const timeRange = validateShiftTimeRange({ startMs, endMs });
  if (!timeRange.valid) return timeRange;

  if (nowMs >= endMs) {
    return validationResult(
      false,
      "shift_ended",
      "Shift has already ended"
    );
  }

  const windowHours =
    Number(
      activationWindowHours
    );

  const safeWindowHours =
    Number.isFinite(
      windowHours
    )
      ? windowHours
      : 2;

  const windowOpenAt =
    startMs -
    safeWindowHours *
      60 *
      60 *
      1000;

  if (nowMs < windowOpenAt) {
    return validationResult(
      false,
      "activation_window_closed",
      "Activation window not open"
    );
  }

  return validationResult(
    true
  );
}

export function assertShiftActivation(
  args
) {
  const result =
    validateShiftActivation(
      args
    );

  if (!result.valid) {
    const error =
      new Error(
        result.message
      );

    error.code =
      result.code;

    throw error;
  }

  return true;
}

/* ─────────────────────────────
   DIRECT OFFER ELIGIBILITY
───────────────────────────── */

export function isActiveDirectOfferStatus(
  value
) {
  const status =
    normalizeShiftStatus(
      value
    );

  return (
    status ===
      SHIFT_STATUS.OFFERED ||
    status ===
      SHIFT_STATUS.OPEN
  );
}